import React, { useState, useCallback, useEffect } from 'react';
import { Edit3, Trash2, Check, X, Upload, User, Crop } from 'react-feather';
import { Creator } from '../../types';
import { getPlatformConfig } from '../../utils/platformConfig';
import parseHandle from '../../utils/handles';
import { validateUrl as isSafeUrl } from '../../utils/security';
import { showErrorToast } from '../../hooks/useToast';
import { FaGlobe } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useUser } from '../../contexts/UserContext';
import { Input } from '../../components/shared/ui/Input';
import { useOptimizedImage } from '../../hooks/useOptimizedImage';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

function isLikelyWebUrl(input: string): boolean {
  try {
    const raw = (input || '').trim();
    if (!raw) return false;
    const withProto = raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
    const url = new URL(withProto);
    const protoOk = url.protocol === 'http:' || url.protocol === 'https:';
    if (!protoOk) return false;
    const host = (url.hostname || '').toLowerCase();
    if (!host) return false;
    // Must resemble a real public host: contains a dot or is a valid IPv4, and not localhost
    const hasDot = host.includes('.');
    const isIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
    const isLocal = host === 'localhost' || host.endsWith('.local') || host.endsWith('.localdomain');
    if (!hasDot && !isIPv4) return false;
    if (isLocal) return false;
    return true;
  } catch {
    return false;
  }
}

export interface CreatorCardProps {
  creator: Creator;
  onUpdate: (creator: Creator) => void;
  onDelete: () => void;
  role: 'admin' | 'viewer';
  onCropImageClick: () => void;
  onUploadImage: (file: File, id: string) => void;
  onImageDelete: (id: string) => void;
  imagePriority?: boolean; // Optional: prioritize image loading for first few cards
}

const fieldConfigs = [
  { label: 'Rates', key: 'rates', type: 'number', prefix: '$' },
  { label: 'Deliverables', key: 'deliverables', type: 'text' },
  { label: 'Reason', key: 'reason', type: 'text' }
] as const;

type CreatorEditable = Omit<Creator, 'id' | 'image'> & { id: string; image: string | null; profileUrls: string[] };

const CreatorCard: React.FC<CreatorCardProps> = React.memo(({
  creator,
  onUpdate,
  onDelete,
  onCropImageClick,
  onUploadImage,
  onImageDelete,
  imagePriority = false
}) => {
  const { canEditSection } = useUser();
  const canEdit = canEditSection('creators');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<CreatorEditable>({
    ...creator,
    image: creator.image || null,
    profileUrls: creator.profileUrls && creator.profileUrls.length ? creator.profileUrls : ['']
  });
  const [clickedButton, setClickedButton] = useState<'accept' | 'decline' | null>(null);


  useEffect(() => {
    if (!canEdit && isEditing) {
      setIsEditing(false);
    }
  }, [canEdit, isEditing]);

  // Always re-extract handle when URL changes (not just when handle is empty)
  useEffect(() => {
    if (isEditing && editData.profileUrls.length > 0) {
      const profileUrl = editData.profileUrls[0];
      if (profileUrl) {
        const parsed = parseHandle(profileUrl);
        // Always update handle when URL changes (re-extract on URL change)
        if (parsed.handle && parsed.platform !== 'unknown') {
          setEditData(prev => ({
            ...prev,
            handle: parsed.handle || ''
          }));
        }
      }
    }
  }, [isEditing, editData.profileUrls]);

  const handleImageUpload = useCallback((file: File) => {
    if (!file) return;
    if (onUploadImage) {
      onUploadImage(file, creator.id);
      return;
    }
  }, [onUploadImage, creator.id]);

  useEffect(() => {
    setEditData(prev => ({
      ...prev,
      image: creator.image || creator.profileImage || null,
    }));
  }, [creator.image, creator.profileImage]);

  // CRITICAL FIX: Sync editData when creator prop changes (after save/update)
  // This ensures the handle and other fields are displayed correctly after editing
  useEffect(() => {
    if (!isEditing) {
      setEditData({
        ...creator,
        image: creator.image || null,
        profileUrls: creator.profileUrls && creator.profileUrls.length ? creator.profileUrls : [''],
        handle: creator.handle || ''
      });
    }
  }, [creator, isEditing]);

  const handleSave = useCallback(() => {
    // Normalize and validate URLs (first URL required)
    const rawUrls = editData?.profileUrls ? editData.profileUrls.filter(u => (u || '').trim() !== '') : [];
    const urls = rawUrls.map(u => (u.startsWith('http://') || u.startsWith('https://')) ? u : `https://${u}`);
    if (urls.length === 0) {
      showErrorToast('Invalid URL', 'Profile URL is required');
      return;
    }
    const firstUrl = urls[0];
    if (!isSafeUrl(firstUrl) || !isLikelyWebUrl(firstUrl)) {
      showErrorToast('Invalid URL', 'Please enter a valid public profile URL');
      return;
    }

    const profileUrl = firstUrl;
    const parsed = parseHandle(profileUrl);

    const next: Creator = {
      ...editData,
      profileUrls: urls,
      handle: parsed && parsed.platform !== 'unknown' && parsed.handle ? parsed.handle : (editData.handle || '')
    } as Creator;

    // ✅ CRITICAL FIX: Exit edit mode immediately after calling onUpdate
    // This ensures UI updates immediately, even if save is async
    setIsEditing(false);
    onUpdate(next);
  }, [editData, onUpdate]);


  // Optimized image loading for creator profile images
  // Priority is determined by parent component (first 2 get priority)
  const imageOpts = useOptimizedImage({
    src: editData.image || creator.profileImage || '',
    priority: imagePriority, // Use prop to prioritize first few images
    rootMargin: '200px' // Start loading 200px before viewport
  });

  // Scroll-triggered fade-in animation
  const scrollAnim = useScrollAnimation({
    threshold: 0.1,
    rootMargin: '50px',
    triggerOnce: true,
    staggerDelay: 0
  });

  return (
    <motion.div
      ref={scrollAnim.ref}
      style={scrollAnim.style}
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }}
      className="relative group creator-card-container"
    >
      {/* Main Card */}
      <div className="relative creator-card">
        {/* Card content */}
        <div className="relative z-10">
          {/* Approval Status Badge - Top Right Corner (Visible to Both Admin & Viewer) */}
          {creator.viewerApproval && creator.viewerApproval !== 'pending' && (
            <motion.div
              initial={{ scale: 0, rotate: -180, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 15,
                duration: 0.6
              }}
              className={`absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-[0_10px_22px_color-mix(in_srgb,var(--shadow) 48%,transparent)] border border-[color-mix(in_srgb,var(--bg-elev) 40%, #ffffff 60%)] ${
                creator.viewerApproval === 'accepted'
                  ? 'bg-success'
                  : 'bg-danger'
              }`}
              whileHover={{ scale: 1.15, rotate: 360 }}
            >
              {creator.viewerApproval === 'accepted' ? (
                <Check size={20} strokeWidth={3} className="text-white" />
              ) : (
                <X size={20} strokeWidth={3} className="text-white" />
              )}
            </motion.div>
          )}

          <div className="p-6">
            <div className={`flex flex-col items-center space-y-4`}>
              {/* Profile Image Section */}
              <div className="relative group/image">
                <motion.div
                  className="w-[100px] h-[100px] rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-blue-500 profile-ring"
                  whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.4 }}
                >
                  {editData.image || creator.profileImage ? (
                    <img
                      ref={imageOpts.ref}
                      src={imageOpts.src}
                      alt={`${editData.handle || 'Creator'} profile`}
                      loading={imageOpts.loading}
                      decoding={imageOpts.decoding}
                      fetchPriority={imageOpts.fetchPriority}
                      onLoad={imageOpts.onLoad}
                      style={imageOpts.style}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[radial-gradient(circle_at_center,color-mix(in_srgb,var(--accent-primary) 35%,transparent) 0%, color-mix(in_srgb,var(--bg-elev) 90%,transparent) 85%)]">
                      <User size={48} className="text-white" />
                    </div>
                  )}
                </motion.div>

                {isEditing && canEdit && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[color-mix(in_srgb,#000000_58%,transparent)] opacity-0 group-hover/image:opacity-100 transition-opacity duration-200 rounded-full backdrop-blur-sm">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2.5 bg-card text-accent rounded-full shadow-elev1 hover:bg-tableHover transition-colors"
                      onClick={() => {
                        const fileInput = document.createElement('input');
                        fileInput.type = 'file';
                        fileInput.accept = 'image/*';
                        fileInput.onchange = (e) => {
                          const input = e.target as HTMLInputElement | null;
                          if (input && input.files && input.files[0]) {
                            handleImageUpload(input.files[0]);
                          }
                        };
                        fileInput.click();
                      }}
                      title="Upload image"
                    >
                      <Upload size={16} />
                    </motion.button>
                  </div>
                )}

                {editData.image && isEditing && canEdit && (
                  <div className="absolute -top-2 -right-2 flex space-x-1">
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 15 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 bg-card text-accent rounded-full shadow-elev1 hover:bg-tableHover transition-colors ring-2 ring-[color-mix(in_srgb,var(--accent) 32%, transparent)]"
                      onClick={onCropImageClick}
                      title="Crop image"
                    >
                      <Crop size={14} />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: -15 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 bg-card text-danger rounded-full shadow-elev1 hover:bg-[color-mix(in_srgb,var(--color-danger) 14%, transparent)] transition-colors ring-2 ring-[color-mix(in_srgb,var(--color-danger) 28%, transparent)]"
                      onClick={() => onImageDelete(creator.id)}
                      title="Delete image"
                    >
                      <Trash2 size={14} />
                    </motion.button>
                  </div>
                )}
              </div>

              {/* Handle Display - Between Image and Social Icons */}
              {!isEditing && creator.handle && (
                <div className="text-center w-full mt-4 mb-2">
                  <p className="text-lg font-semibold text-textPrimary">
                    {creator.handle}
                  </p>
                </div>
              )}

              {/* Handle and Profile URLs */}
              {isEditing ? (
                <div className="text-center w-full space-y-3">
                  <Input
                    type="text"
                    value={editData.handle || ''}
                    onChange={(e) => setEditData({ ...editData, handle: e.target.value })}
                    className="text-lg border border-border focus:ring-2 focus:ring-[var(--focus-ring)] focus:border-[var(--focus-ring)] hover:border-border focus:outline-none input-surface text-center w-full text-textPrimary font-semibold rounded-xl px-3 py-2 transition-all duration-200 shadow-elev1"
                    placeholder="@username"
                    aria-label="Edit creator handle"
                  />
                  {editData.profileUrls.map((url, idx) => {
                    const Icon = getPlatformConfig(url).icon;
                    const platformColor = getPlatformConfig(url).color;
                    
                    // White box background for all modes, smaller box size
                    let iconBgColor = '#ffffff';
                    let iconColor = platformColor;
                    
                    // All icons use brand colors on white background
                    if (platformColor === '#000000') {
                      // TikTok and X: black icons visible on white
                      iconColor = '#000000';
                    } else {
                      // All colored icons: brand colors visible on white
                      iconColor = platformColor;
                    }
                    
                    return (
                      <div key={idx} className="flex items-center space-x-2">
                        <motion.div 
                          whileHover={{ scale: 1.1, rotate: 360 }} 
                          transition={{ duration: 0.3 }}
                          className="rounded-lg p-1"
                          style={{ backgroundColor: iconBgColor }}
                        >
                          <Icon size={28} color={iconColor} />
                        </motion.div>
                        <Input
                          type="url"
                          value={url}
                          onChange={e => {
                            const newUrls = [...editData.profileUrls];
                            newUrls[idx] = e.target.value;

                            // Always re-extract handle when URL changes (not just when handle is empty)
                            if (e.target.value) {
                              try {
                                const parsed = parseHandle(e.target.value);
                                if (parsed.handle && parsed.platform !== 'unknown') {
                                  setEditData({
                                    ...editData,
                                    profileUrls: newUrls,
                                    handle: parsed.handle // Always update handle when URL changes
                                  });
                                  return;
                                }
                              } catch (error) {
                                // If parsing fails, still update URLs but don't update handle
                              }
                            }

                            setEditData({ ...editData, profileUrls: newUrls });
                          }}
                          className="flex-1 border border-border focus:ring-2 focus:ring-[var(--focus-ring)] focus:border-[var(--focus-ring)] hover:border-border focus:outline-none input-surface text-textPrimary text-sm px-3 py-2 rounded-xl transition-all duration-200 shadow-elev1"
                          placeholder="Profile URL (required)"
                          required
                        />
                        {idx === 0 && url.trim().length > 0 && !(isSafeUrl(url.startsWith('http') ? url : `https://${url}`) && isLikelyWebUrl(url)) && (
                          <div className="text-xs text-red-500 mt-1 ml-2">Enter a valid public URL (http/https)</div>
                        )}
                        {editData.profileUrls.length > 1 && (
                          <motion.button
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                            type="button"
                            onClick={() => {
                              const newUrls = editData.profileUrls.filter((_, i) => i !== idx);
                              setEditData({ ...editData, profileUrls: newUrls });
                            }}
                            className="text-danger hover:text-[color-mix(in_srgb,var(--color-danger) 80%, #ffffff 20%)] p-1.5 rounded-lg hover:bg-[color-mix(in_srgb,var(--color-danger) 12%, transparent)] transition-colors"
                            title="Remove this link"
                          >
                            <Trash2 size={16} />
                          </motion.button>
                        )}
                        {idx === editData.profileUrls.length - 1 && editData.profileUrls.length < 4 && (
                          <motion.button
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                            type="button"
                            onClick={() => setEditData({ ...editData, profileUrls: [...editData.profileUrls, ''] })}
                            className="text-success hover:text-[color-mix(in_srgb,var(--color-success) 84%, #ffffff 16%)] p-1.5 rounded-lg hover:bg-[color-mix(in_srgb,var(--color-success) 12%, transparent)] transition-colors font-bold text-lg"
                            title="Add another link"
                          >
                            +
                          </motion.button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap justify-center items-center gap-2">
                  {creator.profileUrls && creator.profileUrls.length > 0 && (
                    creator.profileUrls.slice(0, 4).map((url, idx) => {
                      const { icon: Icon, color: platformColor } = getPlatformConfig(url);
                      
                      // White box background for all modes, smaller box size
                      let iconBgColor = '#ffffff';
                      let iconColor = platformColor;
                      
                      // All icons use brand colors on white background
                      if (platformColor === '#000000') {
                        // TikTok and X: black icons visible on white
                        iconColor = '#000000';
                      } else {
                        // All colored icons: brand colors visible on white
                        iconColor = platformColor;
                      }
                      
                      const IconComponent = Icon || FaGlobe;
                      const safeHref = (isSafeUrl(url) && isLikelyWebUrl(url)) ? url : undefined;
 
                       return (
                         <motion.a
                           key={creator.id + '-' + idx}
                           href={safeHref || '#'}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-lg shadow-[0_4px_12px_color-mix(in_srgb,var(--shadow) 32%,transparent)] hover:shadow-[0_6px_16px_color-mix(in_srgb,var(--shadow) 45%,transparent)] transition-all duration-300 border border-[color-mix(in_srgb,var(--border-subtle) 70%, transparent)]"
                           style={{ backgroundColor: iconBgColor }}
                           title={url}
                           onClick={(e) => {
                             if (!safeHref) {
                               e.preventDefault();
                               showErrorToast('Invalid URL', 'Cannot open this link');
                             }
                           }}
                           whileHover={{ scale: 1.1, rotate: 10, y: -2 }}
                           whileTap={{ scale: 0.95 }}
                         >
                           {IconComponent && <IconComponent size={24} color={iconColor} />}
                         </motion.a>
                       );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Fields Section */}
          <div className="mt-6 space-y-3">
              {fieldConfigs.map((field) => (
              <motion.div
                key={field.key}
                className="bg-[color-mix(in_srgb,var(--bg-card) 30%, transparent)] backdrop-blur-sm rounded-2xl p-4 transition-all duration-300"
                whileHover={{
                  backgroundColor: 'color-mix(in srgb, var(--bg-elev) 88%, var(--ring) 12%)',
                  x: 8,
                  transition: { duration: 0.3 }
                }}
              >
                <label className="block text-xs font-bold text-textMuted uppercase tracking-wider mb-1.5">
                    {field.label}
                  </label>
                  {isEditing ? (
                    <Input
                      type={field.type}
                      value={editData[field.key] || ''}
                      onChange={(e) => setEditData({...editData, [field.key]: e.target.value})}
                      className="w-full px-3 py-2 border border-border focus:ring-2 focus:ring-[var(--focus-ring)] focus:border-[var(--focus-ring)] hover:border-border focus:outline-none input-surface text-textPrimary font-semibold rounded-xl transition-all duration-200 shadow-elev1"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      aria-label={`Edit ${field.label.toLowerCase()}`}
                    />
                  ) : (
                  <p className="text-textPrimary font-semibold text-base">
                      {field.key === 'rates' && creator[field.key] ? `$${parseInt(String(creator[field.key])).toLocaleString()}` : creator[field.key] || 'Not specified'}
                    </p>
                  )}
              </motion.div>
              ))}
            </div>
          </div>

        {/* Action Buttons */}
        <div className="border-t border-border rounded-b-[30px] overflow-hidden">
          {canEdit ? (
            // Admin buttons - ensure rounded bottom corners
            <div className="px-4 py-3 bg-gradient-to-r from-surface to-card flex justify-end space-x-2 rounded-b-[30px]">
              {isEditing ? (
                <>
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={e => { e.stopPropagation(); handleSave(); }}
                    className="p-2.5 text-textInverse bg-success hover:bg-[color-mix(in_srgb,var(--color-success) 88%, #ffffff 12%)] rounded-xl transition-colors shadow-elev1"
                    title="Save changes"
                    aria-label="Save changes"
                  >
                    <Check size={18} />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={e => { e.stopPropagation(); setIsEditing(false); }}
                    className="p-2.5 text-textPrimary bg-[color-mix(in_srgb,var(--bg-surface) 82%, transparent)] hover:bg-[color-mix(in_srgb,var(--bg-surface) 92%, transparent)] rounded-xl transition-colors shadow-elev1"
                    title="Cancel editing"
                    aria-label="Cancel editing"
                  >
                    <X size={18} />
                  </motion.button>
                </>
              ) : (
                <>
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={e => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                    className="p-2.5 text-textInverse bg-accent hover:bg-accentStrong rounded-xl transition-colors shadow-elev1"
                    title="Edit creator"
                    aria-label="Edit creator"
                  >
                    <Edit3 size={18} />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={e => { e.stopPropagation(); onDelete(); }}
                    className="p-2.5 text-textInverse bg-danger hover:bg-[color-mix(in_srgb,var(--color-danger) 88%, #ffffff 12%)] rounded-xl transition-colors shadow-elev1"
                    title="Delete creator"
                    aria-label="Delete creator"
                  >
                    <Trash2 size={18} />
                  </motion.button>
                </>
              )}
            </div>
          ) : (
            // Viewer buttons (Accept/Decline) - 50/50 split
            <div className="flex w-full overflow-hidden rounded-b-[30px]">
              <motion.button
                data-test="creator-accept"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={e => {
                  e.stopPropagation();
                  performance.mark('creator_accept_start');
                  setClickedButton('accept');
                  setTimeout(() => {
                    setClickedButton(null);
                    performance.mark('creator_accept_end');
                    performance.measure('creator_accept_latency', 'creator_accept_start', 'creator_accept_end');
                  }, 600);
                  onUpdate({ ...creator, viewerApproval: 'accepted' });
                }}
                disabled={creator.viewerApproval === 'accepted'}
                animate={
                  clickedButton === 'accept'
                    ? { scale: 1.05 }
                    : {}
                }
                transition={{ duration: 0.4 }}
                className={`flex-1 flex items-center justify-center px-0 py-4 transition-all duration-200 font-bold text-base rounded-none rounded-bl-[30px] ${
                  creator.viewerApproval === 'accepted'
                    ? 'bg-success text-textInverse cursor-default shadow-none'
                    : clickedButton === 'accept'
                    ? 'bg-[color-mix(in_srgb,var(--color-success) 24%, var(--bg-card) 76%)] text-success'
                    : 'bg-[color-mix(in_srgb,var(--color-success) 16%, var(--bg-card) 84%)] text-success hover:bg-[color-mix(in_srgb,var(--color-success) 24%, var(--bg-card) 76%)] active:bg-[color-mix(in_srgb,var(--color-success) 28%, var(--bg-card) 72%)]'
                }`}
                title="Accept this creator"
                aria-label="Accept creator"
              >
                <span className="inline-flex items-center gap-2">
                  <Check size={20} strokeWidth={3} />
                  {creator.viewerApproval === 'accepted' ? 'Accepted' : 'Accept'}
                </span>
              </motion.button>
              <motion.button
                data-test="creator-decline"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={e => {
                  e.stopPropagation();
                  performance.mark('creator_decline_start');
                  setClickedButton('decline');
                  setTimeout(() => {
                    setClickedButton(null);
                    performance.mark('creator_decline_end');
                    performance.measure('creator_decline_latency', 'creator_decline_start', 'creator_decline_end');
                  }, 600);
                  onUpdate({ ...creator, viewerApproval: 'declined' });
                }}
                disabled={creator.viewerApproval === 'declined'}
                animate={
                  clickedButton === 'decline'
                    ? { scale: 1.05 }
                    : {}
                }
                transition={{ duration: 0.4 }}
                className={`flex-1 flex items-center justify-center px-0 py-4 transition-all duration-200 font-bold text-base rounded-none rounded-br-[30px] ${
                  creator.viewerApproval === 'declined'
                        ? 'bg-danger text-textInverse cursor-default shadow-none'
                    : clickedButton === 'decline'
                    ? 'bg-[color-mix(in_srgb,var(--color-danger) 24%, var(--bg-card) 76%)] text-danger'
                    : 'bg-[color-mix(in_srgb,var(--color-danger) 16%, var(--bg-card) 84%)] text-danger hover:bg-[color-mix(in_srgb,var(--color-danger) 24%, var(--bg-card) 76%)] active:bg-[color-mix(in_srgb,var(--color-danger) 28%, var(--bg-card) 72%)]'
                }`}
                title="Decline this creator"
                aria-label="Decline creator"
              >
                <span className="inline-flex items-center gap-2">
                  <X size={20} strokeWidth={3} />
                  {creator.viewerApproval === 'declined' ? 'Declined' : 'Decline'}
                </span>
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
});

CreatorCard.displayName = 'CreatorCard';

export default CreatorCard; 
